import { Button, Fade, Stack, Typography, useTheme } from "@mui/material";
import { ControlledBigTooltip } from "components/ControlledBigTooltip";
import { useState } from "react";

import { AttentionItem } from "./attentionItems";
import { useAttentionTravel } from "./AttentionTargets";

/**
 * How one reason reads as a heading. The array's order is the order the reviewer
 * meets the groups in, and a kind absent from it is not rendered at all.
 *
 * Supplied by the form rather than fixed here, because the reasons differ: an
 * extraction review has readings nobody checked, a manifest ingest has sources
 * that disagree. What both want is validation first — it is the only reason
 * saying a value is *wrong* rather than worth a look — and the rest ordered by
 * how much is at stake.
 */
export interface AttentionSection<Kind extends string> {
  kind: Kind;
  title: string;
}

/**
 * Everything wanting the reviewer's attention, in one control beside Confirm.
 *
 * Three lists were three answers to "what is left?" — the validation issues, the
 * record differences, and the flagged readings — each with its own button, its own
 * count and its own colour, none of which added up to a number the reviewer could
 * work down. They all came from one walk already (`listAttention`), so this is
 * that walk rendered once, grouped by reason, every row travelling to the control
 * it names.
 *
 * Error-coloured only when a kind named by `errorKinds` is present — the
 * validator's objections. Every other reason is a question for the reviewer
 * rather than a rule they have broken, and colouring those red said otherwise.
 */
export function AttentionListButton<Kind extends string>({
  attention,
  sections,
  errorKinds,
}: {
  attention: readonly AttentionItem<Kind>[];
  sections: readonly AttentionSection<Kind>[];
  /** Kinds that turn the control error-coloured. Usually the validator's. */
  errorKinds?: readonly Kind[];
}) {
  const theme = useTheme();
  const { travelTo } = useAttentionTravel();
  const [open, setOpen] = useState(false);

  if (attention.length === 0) return null;

  const hasIssue = attention.some((item) => errorKinds?.includes(item.kind) ?? false);
  // Counted by control, not by reason: a field flagged for two reasons is one
  // place to go and one thing to settle, so counting the reasons promised a
  // number the reviewer could never work down from the list. An issue with
  // nowhere to travel to still counts — it is on the list and still has to be
  // dealt with.
  // Keyed by position for an item with nowhere to travel to: two issues that
  // match no control and read the same are still two things to deal with, and
  // keying them by their text collapsed them into one.
  const stops = new Set(attention.map((item, index) => item.path || `orphan:${index}`)).size;
  const label = `${stops} to resolve`;

  return (
    <ControlledBigTooltip
      open={open}
      onClose={() => setOpen(false)}
      placement="top-end"
      // The theme makes every tooltip non-interactive, which puts
      // `pointer-events: none` on the popper and would drop every click through
      // to the form behind it.
      disableInteractive={false}
      slots={{ transition: Fade }}
      slotProps={{ tooltip: { sx: { maxWidth: theme.spacing(56) } } }}
      title={
        <Stack gap={1.25} sx={{ maxHeight: theme.spacing(75), overflowY: "auto" }}>
          {sections.map(({ kind, title }) => {
            const rows = attention.filter((item) => item.kind === kind);
            if (rows.length === 0) return null;
            return (
              <Stack key={kind} gap={0.5}>
                <Typography variant="subtitle1" fontWeight="bold" fontSize="12px">
                  {title}
                </Typography>
                <Stack component="ul" gap={0.5} sx={{ pl: 2, m: 0 }}>
                  {rows.map((row, index) => (
                    <li key={`${row.kind}:${row.path}:${row.label}:${row.detail ?? ""}:${index}`}>
                      <Typography
                        component={row.path === "" ? "span" : "button"}
                        type={row.path === "" ? undefined : "button"}
                        variant="caption"
                        onClick={
                          row.path === ""
                            ? undefined
                            : () => {
                                setOpen(false);
                                travelTo(row.path);
                              }
                        }
                        sx={{
                          appearance: "none",
                          border: "none",
                          background: "none",
                          p: 0,
                          m: 0,
                          font: "inherit",
                          fontSize: "12px",
                          color: "inherit",
                          textAlign: "left",
                          // An issue keyed to nothing on this form has no control
                          // to travel to, so it reads as text rather than
                          // pretending to be a link.
                          cursor: row.path === "" ? "default" : "pointer",
                          ...(row.path === ""
                            ? {}
                            : { "&:hover": { textDecoration: "underline" } }),
                        }}
                      >
                        {row.detail == null ? row.label : `${row.label} — ${row.detail}`}
                      </Typography>
                    </li>
                  ))}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      }
    >
      <Button
        type="button"
        color={hasIssue ? "error" : "warning"}
        variant="text"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {label}
      </Button>
    </ControlledBigTooltip>
  );
}
